import { computed, Injectable, signal } from '@angular/core';
import { PaginationResponse } from '@iq-central-front-end/share';

export interface StaffState {
    staffs: PaginationResponse<any> | null;
    staff: any | null;
    activeStaffs: PaginationResponse<any> | null;
    enrolledStaffs: any[] | null;
    enrolledCourses: any | null;
    assignedCourses: any | null;
    assignedLearningPaths: any | null;
    savedCourses: any | null;
    likedCourses: any | null;
    dueUsers: any[] | null;
    loading: boolean;
    error: string | null;
    searchQuery: string;
    currentPage: number;
    pageSize: number;
}

@Injectable({ providedIn: 'root' })
export class StaffStore {
    private readonly state = signal<StaffState>({
        staffs: null,
        staff: null,
        activeStaffs: null,
        enrolledStaffs: null,
        enrolledCourses: null,
        assignedCourses: null,
        assignedLearningPaths: null,
        savedCourses: null,
        likedCourses: null,
        dueUsers: null,
        loading: false,
        error: null,
        searchQuery: '',
        currentPage: 1,
        pageSize: 6,
    });

    readonly staffs$ = computed(() => this.state().staffs);
    readonly staff$ = computed(() => this.state().staff);
    readonly activeStaffs$ = computed(() => this.state().activeStaffs);
    readonly enrolledStaffs$ = computed(() => this.state().enrolledStaffs);
    readonly dueUsers$ = computed(() => this.state().dueUsers);
    readonly enrolledCourses$ = computed(() => this.state().enrolledCourses);
    readonly assignedCourses$ = computed(() => this.state().assignedCourses);
    readonly assignedLearningPaths$ = computed(() => this.state().assignedLearningPaths);
    readonly savedCourses$ = computed(() => this.state().savedCourses);
    readonly likedCourses$ = computed(() => this.state().likedCourses);
    readonly loading$ = computed(() => this.state().loading);
    readonly error$ = computed(() => this.state().error);
    readonly searchQuery$ = computed(() => this.state().searchQuery);
    readonly currentPage$ = computed(() => this.state().currentPage);
    readonly pageSize$ = computed(() => this.state().pageSize);

    setLoading(loading: boolean): void {
        this.state.update((state) => ({ ...state, loading }));
    }

    setError(error: string | null): void {
        this.state.update((state) => ({ ...state, error }));
    }

    setDueUsers(dueUsers: any[]): void {
        this.state.update((state) => ({ ...state, dueUsers, loading: false }));
    }

    setStaffs(staffs: PaginationResponse<any>): void {
        this.state.update((state) => ({ ...state, staffs, loading: false }));
    }

    setStaff(staff: any): void {
        this.state.update((state) => ({
            ...state,
            staff,
            loading: false,
        }));
    }

    setActiveStaffs(activeStaffs: PaginationResponse<any>): void {
        this.state.update((state) => ({ ...state, activeStaffs, loading: false }));
    }

    setEnrolledStaffs(enrolledStaffs: any[]): void {
        this.state.update((state) => ({ ...state, enrolledStaffs, loading: false }));
    }

    setEnrolledCourses(enrolledCourses: any): void {
        this.state.update((state) => ({ ...state, enrolledCourses }));
    }

    setAssignedCourses(assignedCourses: any): void {
        this.state.update((state) => ({ ...state, assignedCourses }));
    }

    setAssignedLearningPaths(assignedLearningPaths: any): void {
        this.state.update((state) => ({ ...state, assignedLearningPaths }));
    }

    setSavedCourses(savedCourses: any): void {
        this.state.update((state) => ({ ...state, savedCourses }));
    }

    setLikedCourses(likedCourses: any): void {
        this.state.update((state) => ({ ...state, likedCourses }));
    }

    addStaff(staff: any): void {
        this.state.update((state) => ({
            ...state,
            staffs: state.staffs ? {
                ...state.staffs,
                data: [...state.staffs.data, staff],
                total: state.staffs.total + 1
            } : null,
            loading: false,
        }));
    }

    updateStaff(updatedStaff: any): void {
        this.state.update((state) => ({
            ...state,
            staffs: state.staffs ? {
                ...state.staffs,
                data: state.staffs.data.map(staff =>
                    staff.id === updatedStaff.id ? updatedStaff : staff
                )
            } : null,
            staff: state.staff?.id === updatedStaff.id ? updatedStaff : state.staff,
            loading: false,
        }));
    }

    deleteStaff(staffId: string): void {
        this.state.update((state) => ({
            ...state,
            staffs: state.staffs ? {
                ...state.staffs,
                data: state.staffs.data.filter(staff => staff.id !== staffId),
                total: state.staffs.total - 1
            } : null,
            staff: state.staff?.id === staffId ? null : state.staff,
            loading: false,
        }));
    }

    setSearchQuery(searchQuery: string): void {
        this.state.update((state) => ({ ...state, searchQuery }));
    }

    setCurrentPage(currentPage: number): void {
        this.state.update((state) => ({ ...state, currentPage }));
    }

    setPageSize(pageSize: number): void {
        this.state.update((state) => ({ ...state, pageSize }));
    }

    clearError(): void {
        this.state.update((state) => ({ ...state, error: null }));
    }

    resetPagination(): void {
        this.state.update((state) => ({ ...state, currentPage: 1, pageSize: 6, searchQuery: '' }));
    }

    reset(): void {
        this.state.set({
            staffs: null,
            staff: null,
            activeStaffs: null,
            enrolledStaffs: null,
            enrolledCourses: null,
            assignedCourses: null,
            assignedLearningPaths: null,
            savedCourses: null,
            likedCourses: null,
            dueUsers: null,
            loading: false,
            error: null,
            searchQuery: '',
            currentPage: 1,
            pageSize: 6,
        });
    }
}
